import * as THREE from 'three';

import type { IMeasurement } from '@core';

import {
    ANGLE_AXES,
    DISPLACEMENT_AXES,
    JOINT_ANGLE_AXES,
    VISUALIZATION_AXES_PARENT_MAP,
    VISUALIZATION_MEASUREMENT_TYPES,
    VISUALIZATION_MEASUREMENTS,
} from './mappings';

import type { FrameFirstSkeleton, SkeletonFrame } from './skeletonUtils';
import type { FocusedMeasurement, KeypointIdToName } from './types';

export const extendLine = (start: number[], end: number[], scale = 1) => {
    const startVec = new THREE.Vector3(start[0], start[1], start[2]);
    const endVec = new THREE.Vector3(end[0], end[1], end[2]);

    const direction = new THREE.Vector3().subVectors(endVec, startVec).normalize();

    const referenceLine1End = new THREE.Vector3().copy(startVec).add(direction.multiplyScalar(scale));

    return [startVec, referenceLine1End];
};

export const createDirectionalLine = (
    points: [number[], number[]?],
    direction = [0, 1, 0],
    bidirectional = false,
    scale = 10,
) => {
    // Two points provided
    if (points[1]) {
        const point1 = new THREE.Vector3(points[0][0], points[0][1], points[0][2]);
        const point2 = new THREE.Vector3(points[1][0], points[1][1], points[1][2]);
        const directionVec = new THREE.Vector3().subVectors(point2, point1).normalize();

        if (!bidirectional) {
            const lineEnd = new THREE.Vector3().copy(point1).add(directionVec.multiplyScalar(scale));
            return [point1, lineEnd];
        }

        // Extend in both directions from both points
        const lineStart = new THREE.Vector3().copy(point1).add(directionVec.clone().multiplyScalar(-scale));
        const lineEnd = new THREE.Vector3().copy(point2).add(directionVec.clone().multiplyScalar(scale));

        return [lineStart, lineEnd];
    }

    // Single point provided
    const pointVec = new THREE.Vector3(points[0][0], points[0][1], points[0][2]);
    const directionVec = new THREE.Vector3(direction[0], direction[1], direction[2]).normalize();

    if (!bidirectional) {
        const lineEnd = new THREE.Vector3().copy(pointVec).add(directionVec.multiplyScalar(scale));
        return [pointVec, lineEnd];
    }

    const lineStart = new THREE.Vector3().copy(pointVec).add(directionVec.clone().multiplyScalar(-scale));
    const lineEnd = new THREE.Vector3().copy(pointVec).add(directionVec.clone().multiplyScalar(scale));

    return [lineStart, lineEnd];
};

// Find the midpoint between two 3D points
const findMidPoint = (point1: number[], point2: number[]): number[] => {
    const midX = (point1[0] + point2[0]) / 2;
    const midY = (point1[1] + point2[1]) / 2;
    const midZ = (point1[2] + point2[2]) / 2;

    return [midX, midY, midZ];
};

// Create a directional line from the midpoint of two points
export const createMidPointDirectionalLine = (
    points: [number[], number[]],
    direction = [0, 1, 0],
    bidirectional = false,
    scale = 10,
) => {
    // Find the midpoint
    const midPoint = findMidPoint(points[0], points[1]);

    // Create a vector from the midpoint
    const pointVec = new THREE.Vector3(midPoint[0], midPoint[1], midPoint[2]);
    const directionVec = new THREE.Vector3(direction[0], direction[1], direction[2]).normalize();

    // Create the line in one or both directions
    if (!bidirectional) {
        const lineEnd = new THREE.Vector3().copy(pointVec).add(directionVec.multiplyScalar(scale));
        return [pointVec, lineEnd];
    }

    const lineStart = new THREE.Vector3().copy(pointVec).add(directionVec.clone().multiplyScalar(-scale));
    const lineEnd = new THREE.Vector3().copy(pointVec).add(directionVec.clone().multiplyScalar(scale));

    return [lineStart, lineEnd];
};

export function getKeypointId(keypointName: string, keypointIdToName: KeypointIdToName) {
    const name_to_id: Record<string, string> = Object.entries(keypointIdToName).reduce(
        (acc: Record<string, string>, [id, name]) => {
            acc[name] = id;
            return acc;
        },
        {},
    );

    // Look up the id for the given keypoint
    const id = name_to_id[keypointName];

    if (!id) {
        throw new Error(`Invalid keypoint name: ${keypointName}`);
    }

    return Number(id);
}

export function getKeypointName(keypointId: number | string, keypointIdToName: KeypointIdToName) {
    const name = keypointIdToName[keypointId];

    if (!name) {
        throw new Error(`Invalid keypoint ID: ${keypointId}`);
    }

    return name;
}

export function getSafeSkeleton(
    skeleton: FrameFirstSkeleton,
    frame: number,
    id: number,
): [number, number, number] | null;
export function getSafeSkeleton(skeleton: FrameFirstSkeleton, frame: number): SkeletonFrame | null;
export function getSafeSkeleton(skeleton: FrameFirstSkeleton, frame: number, id?: number) {
    if (typeof id === 'number') {
        if (skeleton[frame]) {
            return skeleton[frame][id];
        }

        if (skeleton[skeleton.length - 1]) {
            return skeleton[skeleton.length - 1][id];
        }

        return null;
    }

    if (skeleton[frame]) {
        return skeleton[frame];
    }

    if (skeleton[skeleton.length - 1]) {
        return skeleton[skeleton.length - 1];
    }

    return null;
}

export const getSafePoint = (skeleton: FrameFirstSkeleton, id: string | number, frame: number) =>
    skeleton[frame]?.[id] ?? skeleton[skeleton.length - 1][id] ?? null;

export function getMeasurementValuesByName(
    measurements: IMeasurement[] | undefined,
    measurementName: string,
    frame: number,
): number[] | null {
    if (!measurements?.length) {
        return null;
    }

    const measurement = measurements.find((m) => m.measurement_name === measurementName);
    if (!measurement?.value) {
        return null;
    }

    const dimension = measurement.value.length; // where i = 0 is x, 1 is y, 2 is z

    // Clamp frame to available data range so measurements stay visible at end of swing
    const maxFrame = measurement.value[0]?.length ? measurement.value[0].length - 1 : 0;
    const clampedFrame = Math.min(frame, maxFrame);

    return Array.from({ length: dimension }, (_, i) => measurement.value[i][clampedFrame]);
}

type FocusAxis =
    | { type: 'displacement'; axis: (typeof DISPLACEMENT_AXES)[keyof typeof DISPLACEMENT_AXES] }
    | { type: 'angle'; axis: (typeof ANGLE_AXES)[keyof typeof ANGLE_AXES] }
    | { type: 'jointAngle'; axis: (typeof JOINT_ANGLE_AXES)[keyof typeof JOINT_ANGLE_AXES] };

function getFocusAxis(focusId: string): FocusAxis | undefined {
    const displacement = DISPLACEMENT_AXES[focusId];

    if (displacement) {
        return { type: 'displacement', axis: displacement };
    }

    const angle = ANGLE_AXES[focusId];

    if (angle) {
        return { type: 'angle', axis: angle };
    }

    const jointAngle = JOINT_ANGLE_AXES[focusId];

    if (jointAngle) {
        return { type: 'jointAngle', axis: jointAngle };
    }

    return undefined;
}

export function parseFocusId(focusId: string | null): FocusedMeasurement {
    if (!focusId) return null;

    const measurementId = VISUALIZATION_AXES_PARENT_MAP?.[focusId];
    const measurement = VISUALIZATION_MEASUREMENTS?.[measurementId];
    const focus = getFocusAxis(focusId);

    if (!measurementId || !focus || !measurement) return null;

    return {
        keypointName: focus.axis.targetPoints[0],
        measurement: measurementId,
        axis: focus.axis.axisIndex as 0 | 1 | 2,
        axisData: focus.axis,
        measurementType: VISUALIZATION_MEASUREMENT_TYPES[measurementId],
        type: focus.type,
        measurementData: measurement,
    };
}
